import React, { useEffect } from 'react';

import {
  // BrowserRouter,
  Route,
  // Routes,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

// import Loader from '../components/Loader';
import PrivateRoute from '../containers/PrivateRoute';
import LoginPage from '../pages/LoginPage';
import OtpSignInPage from '../pages/OtpSignInPage';
import PasswordSignInPage from '../pages/PasswordSignInPage';
import SignInMethodPage from '../pages/SignInMethodPage';
import RequestAccessPage from '../pages/RequestAccessPage';
import DashboardPage from '../pages/DashboardPage';
import RequestAccessSuccessPage from '../pages/RequestAccessSuccessPage';
import RequestNewPasswordPage from '../pages/RequestNewPasswordPage';
import RequestNewPasswordSuccessPage from '../pages/RequestNewPasswordSuccessPage';
import SetNewPasswordPage from '../pages/SetNewPasswordPage';
import ContextSwitchPage from '../pages/ContextSwitchPage';
import AuthLayout from '../containers/AuthLayout';

import CandidatesContextProvider from '../context/CandidatesContext';
import JobsContextProvider from '../context/JobsContext';
import AuthContextProvider from '../context/AuthContext';
import UiContextProvider from '../context/UiContext';
import SearchContextProvider from '../context/SearchContext';
import PreventNavigationProvider from '../context/PreventNavigationContext';
import CompanyContextProvider from '../context/CompanyContext';
// Removed global FeedWatcher; it will be rendered within DashboardPage only

function isFirefox() {
  if (navigator.userAgentData) {
    return navigator.userAgentData.brands.some(
      (brand) => brand.brand === 'Firefox'
    );
  }
  // Fallback to userAgent string check if userAgentData is not available
  return navigator.userAgent.indexOf('Firefox') > -1;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'reactQueryOffilneCache',
  serialize: (persistedClient) => {
    const filteredQueries = persistedClient.queries.filter((query) => {
      // Exclude queries with a specific key or any other condition
      return !query.queryKey.includes('feedPosts');
    });

    return JSON.stringify({
      ...persistedClient,
      queries: filteredQueries,
    });
  },
});

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route
        path="*"
        element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/request-access"
        element={
          <AuthLayout noButton>
            <RequestAccessPage />
          </AuthLayout>
        }
      />
      <Route
        path="/contexts"
        element={
          <PrivateRoute>
            <AuthLayout noButton>
              <ContextSwitchPage />
            </AuthLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/request-access/success"
        element={
          <AuthLayout noButton>
            <RequestAccessSuccessPage />
          </AuthLayout>
        }
      />
      <Route
        path="/login"
        element={
          <AuthLayout noButton>
            <LoginPage />
          </AuthLayout>
        }
      />
      <Route
        path="/login/method"
        element={
          <AuthLayout noButton>
            <SignInMethodPage />
          </AuthLayout>
        }
      />
      <Route
        path="/login/method/otp"
        element={
          <AuthLayout noButton>
            <OtpSignInPage />
          </AuthLayout>
        }
      />
      <Route
        path="/login/method/password"
        element={
          <AuthLayout noButton>
            <PasswordSignInPage />
          </AuthLayout>
        }
      />
      <Route
        path="/request-password"
        element={
          <AuthLayout noButton>
            <RequestNewPasswordPage />
          </AuthLayout>
        }
      />
      <Route
        path="/request-password/success"
        element={
          <AuthLayout noButton>
            <RequestNewPasswordSuccessPage />
          </AuthLayout>
        }
      />
      <Route
        path="/set-password"
        element={
          <AuthLayout noButton>
            <SetNewPasswordPage />
          </AuthLayout>
        }
      />
      <Route
        path="/tools"
        element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tools/:page"
        element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/tools/:page/:subpage/*"
        element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        }
      />
      <Route path="/" element={<Navigate to="/tools" />} />
    </Route>
  )
);

// const doNotPersistQueries = ['newPosts', 'feedPosts'];

const RoutesComponent = () => {
  // Check if the browser is Firefox
  // If it is, set .firefox class on the body
  // to apply firefox scrollbar styles and not to override ::-webkit-scrollbar styles
  useEffect(() => {
    if (isFirefox()) {
      document.body.classList.add('firefox');
    }
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
      // shouldDehydrateQuery={({ queryKey }) => {
      //   return !doNotPersistQueries.includes(queryKey);
      // }}
    >
      <UiContextProvider>
        <AuthContextProvider>
          <PreventNavigationProvider>
            <JobsContextProvider>
              <CandidatesContextProvider>
                <CompanyContextProvider>
                  <SearchContextProvider>
                    <RouterProvider router={router} />
                  </SearchContextProvider>
                </CompanyContextProvider>
              </CandidatesContextProvider>
            </JobsContextProvider>
          </PreventNavigationProvider>
        </AuthContextProvider>
      </UiContextProvider>
    </PersistQueryClientProvider>
  );
};
export default RoutesComponent;
